import React, { useMemo } from 'react';
import _ from 'lodash';
import { useTheme } from '../../context/ThemeContext';
import { LANGUAGES, getDocCountsForLength } from '../../constants';
import ExplanationCard from '../ExplanationCard';

const HeatmapView = ({ selectedMethod, selectedModel, methodData, heatmapMetric, docCounts: propDocCounts }) => {
  const { isDarkMode } = useTheme();

  // Helper function to calculate cell background color for heatmap
  const getHeatmapCellColor = (value, minVal, maxVal, type) => {
    if (isNaN(value) || value === null || value === 'N/A') return isDarkMode ? '#262626' : '#f3f4f6';
    if (type === 'absolute') {
      const normalizedValue = maxVal === minVal ? 0.5 : (value - minVal) / (maxVal - minVal);
      const greenIntensity = Math.floor(normalizedValue * 180) + 70;
      return `rgb(70, ${greenIntensity}, 70)`;
    } else {
      const effectiveMaxAbs = Math.max(Math.abs(minVal), Math.abs(maxVal), 0.001);
      const normalizedValue = value / effectiveMaxAbs;
      if (value > 0) {
        return `rgba(60, 179, 113, ${0.3 + normalizedValue * 0.7})`;
      } else if (value < 0) {
        return `rgba(220, 20, 60, ${0.3 + Math.abs(normalizedValue) * 0.7})`;
      } else {
        return '#f5f5f5'; // Light grey for zero/no change
      }
    }
  };

  // Determine doc counts dynamically based on data
  const docCounts = useMemo(() => {
    if (propDocCounts) return propDocCounts;
    if (!selectedMethod || !selectedModel || !methodData[selectedMethod]?.[selectedModel]) {
      return getDocCountsForLength(7);
    }
    const modelData = methodData[selectedMethod][selectedModel];
    const maxLength = Math.max(
      ...LANGUAGES.map(lang => (modelData[lang]?.length || 0))
    );
    return getDocCountsForLength(maxLength);
  }, [selectedMethod, selectedModel, methodData, propDocCounts]);

  const heatmapDisplayValues = useMemo(() => {
    if (!selectedMethod || !methodData[selectedMethod] || !selectedModel) {
      return { data: [], minGlobal: 0, maxGlobal: 0 };
    }

    const allDisplayValues = [];
    const dataForTable = [];

    if (methodData[selectedMethod][selectedModel]) {
      LANGUAGES.forEach(lang => {
        const values = methodData[selectedMethod][selectedModel][lang] || [];
        values.forEach((value, docIndex) => {
          let displayValue = value;
          if (heatmapMetric === 'improvement' && docIndex > 0) {
            displayValue = value - (values[0] || 0);
          } else if (heatmapMetric === 'relative' && values[0] !== 0) {
            displayValue = ((value - values[0]) / values[0]) * 100;
          }

          if (displayValue !== null && displayValue !== undefined && !isNaN(displayValue)) {
            allDisplayValues.push(displayValue);
          }

          dataForTable.push({
            language: lang,
            docCount: docCounts[docIndex],
            displayValue: (displayValue === null || displayValue === undefined || isNaN(displayValue)) ? 'N/A' : displayValue.toFixed(2),
            rawValue: value
          });
        });
      });
    }

    const minGlobal = allDisplayValues.length > 0 ? Math.min(...allDisplayValues) : 0;
    const maxGlobal = allDisplayValues.length > 0 ? Math.max(...allDisplayValues) : 0;

    return { data: dataForTable, minGlobal, maxGlobal };
  }, [selectedMethod, selectedModel, methodData, heatmapMetric, docCounts]);

  const { data, minGlobal, maxGlobal } = heatmapDisplayValues;

  return (
    <div className="mb-8">
      <ExplanationCard title="Heatmap Analysis Explanation">
        <div className="space-y-2">
          {heatmapMetric === 'absolute' && (
            <div>
              <p><strong>Absolute Values:</strong> Shows the raw performance scores as they are.</p>
              <p>Higher values (darker green) indicate better performance. This view is best for seeing overall magnitude differences between conditions.</p>
            </div>
          )}
          {heatmapMetric === 'improvement' && (
            <div>
              <p><strong>Improvement Values:</strong> Shows the difference from the baseline (0 documents).</p>
              <p>Calculated as: Current Score - Baseline Score. Positive values (green) show improvement, negative values (red) show decline from baseline.</p>
            </div>
          )}
          {heatmapMetric === 'relative' && (
            <div>
              <p><strong>Relative Values:</strong> Shows percentage change from the baseline (0 documents).</p>
              <p>Calculated as: ((Current Score - Baseline Score) / Baseline Score) × 100. Useful for comparing proportional improvements across different scales.</p>
            </div>
          )}
        </div>
      </ExplanationCard>

      <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
        Performance Heatmap Overview
      </h3>
      <p className="text-lg text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
        Showing <span style={{ color: 'var(--violet-500)', fontWeight: 600 }}>{heatmapMetric.charAt(0).toUpperCase() + heatmapMetric.slice(1)}</span> values for {selectedMethod} method - {selectedModel} model.
      </p>

      <div className="p-6 rounded-xl shadow-inner border overflow-x-auto" style={{ backgroundColor: 'var(--chart-bg)', borderColor: 'var(--border-primary)' }}>
        <table className="min-w-full" style={{ borderColor: 'var(--border-secondary)' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <th className="px-5 py-4 text-left text-sm font-semibold uppercase tracking-wider min-w-[180px]" style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}>
                Language
              </th>
              {docCounts.map(count => (
                <th key={count} className="px-5 py-4 text-center text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}>
                  {count} docs
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LANGUAGES.map((lang, langIdx) => {
              const rowData = data.filter(d => d.language === lang);
              if (rowData.length === 0) return null;
              return (
                <tr
                  key={lang}
                  style={{ backgroundColor: langIdx % 2 === 0 ? 'var(--bg-secondary)' : 'transparent' }}
                >
                  <td className="px-5 py-3 font-medium text-sm sticky left-0 z-10" style={{ color: 'var(--text-primary)', backgroundColor: 'inherit', borderColor: 'var(--border-primary)' }}>
                    {lang}
                  </td>
                  {rowData.map((cell, idx) => {
                    const isOptimalHighlight = heatmapMetric === 'absolute' &&
                      cell.rawValue === _.max(methodData[selectedMethod]?.[selectedModel]?.[lang] || []) &&
                      cell.rawValue !== undefined && cell.rawValue !== null;
                    return (
                      <td
                        key={idx}
                        className={`px-5 py-3 text-center text-sm transition-colors duration-200
                                    ${isOptimalHighlight ? 'font-bold ring-2 ring-blue-500 rounded-md scale-105 transform' : ''}`}
                        style={{
                          backgroundColor: getHeatmapCellColor(parseFloat(cell.displayValue), minGlobal, maxGlobal, heatmapMetric),
                          color: isDarkMode ? '#fafafa' : '#111827',
                          borderColor: 'var(--border-primary)'
                        }}
                        title={`Doc Count: ${cell.docCount}\nDisplay Value: ${cell.displayValue}\nRaw Value: ${cell.rawValue?.toFixed(2) || 'N/A'}`}
                      >
                        {cell.displayValue}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HeatmapView;